import ajax from './ajax';

export interface Organization {
    _id: string;
    orgId: string;
    name: string;
    description?: string;
    logo?: string;
    website?: string;
    email?: string;
    phone?: string;
    address?: string;
    city?: string;
    state?: string;
    country?: string;
    zipCode?: string;
    status: string;
    createdAt: string;
    updatedAt: string;
}

export interface OrganizationUser {
    _id: string;
    email: string;
    name?: string;
    profile?: { name?: string; phone?: string; avatar?: string };
    role: string;
    status: string;
    createdAt?: string;
}

export interface UpdateOrganizationData {
    name?: string;
    description?: string;
    logo?: string;
    website?: string;
    email?: string;
    phone?: string;
    address?: string;
    city?: string;
    state?: string;
    country?: string;
    zipCode?: string;
}

export interface OrganizationUserListParams {
    page?: number;
    limit?: number;
    search?: string;
    role?: string;
    status?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
}

export interface OrganizationUserListResponse {
    users: OrganizationUser[];
    total: number;
    page: number;
    limit: number;
}

export interface InviteUserData {
    email: string;
    role: string;
    message?: string;
}

export interface JoinRequest {
    _id: string;
    requestId: string;
    userId: string;
    organizationId: string;
    userEmail: string;
    userName: string;
    status: string;
    createdAt: string;
}

export interface JoinRequestListParams {
    page?: number;
    limit?: number;
    status?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
}

export interface JoinRequestListResponse {
    requests: JoinRequest[];
    total: number;
    page: number;
    limit: number;
}

class OrganizationService {
    async getAllOrganizations(params?: {
        page?: number;
        limit?: number;
        search?: string;
    }): Promise<{ organizations: Organization[]; total: number }> {
        const queryParams = new URLSearchParams();
        if (params?.page) queryParams.append('page', String(params.page));
        if (params?.limit) queryParams.append('limit', String(params.limit));
        if (params?.search) queryParams.append('search', params.search);
        const query = queryParams.toString();
        // Backend returns a bare array
        const response = await ajax.get(`/api/organizations${query ? `?${query}` : ''}`);
        return Array.isArray(response) ? { organizations: response, total: response.length } : response;
    }

    async getCurrentOrganization(): Promise<Organization> {
        return ajax.get('/api/organizations/current');
    }

    async getOrganizationById(id: string): Promise<Organization> {
        return ajax.get(`/api/organizations/${id}`);
    }

    async createOrganization(data: { name: string; slug: string; emailDomain?: string }): Promise<Organization> {
        return ajax.post('/api/organizations', data);
    }

    async updateOrganization(id: string, data: UpdateOrganizationData): Promise<Organization> {
        return ajax.put(`/api/organizations/${id}`, data);
    }

    async getOrganizationUsers(orgId: string, params: OrganizationUserListParams = {}): Promise<OrganizationUserListResponse> {
        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                queryParams.append(key, String(value));
            }
        });
        const query = queryParams.toString();
        return ajax.get(`/api/organizations/${orgId}/users${query ? `?${query}` : ''}`);
    }

    async inviteUser(orgId: string, data: InviteUserData): Promise<{ message: string; inviteId: string }> {
        return ajax.post(`/api/organizations/${orgId}/invite`, data);
    }

    async removeUser(orgId: string, userId: string): Promise<{ message: string }> {
        return ajax.delete(`/api/organizations/${orgId}/users/${userId}`);
    }

    async updateUserRole(orgId: string, userId: string, role: string): Promise<OrganizationUser> {
        return ajax.put(`/api/organizations/${orgId}/users/${userId}/role`, {
            role,
        });
    }

    async getJoinRequests(orgId: string, params: JoinRequestListParams = {}): Promise<JoinRequestListResponse> {
        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                queryParams.append(key, String(value));
            }
        });
        const query = queryParams.toString();
        return ajax.get(`/api/organizations/${orgId}/join-requests${query ? `?${query}` : ''}`);
    }

    async approveJoinRequest(orgId: string, requestId: string): Promise<{ message: string }> {
        return ajax.post(`/api/organizations/${orgId}/join-requests/${requestId}/approve`);
    }

    async rejectJoinRequest(orgId: string, requestId: string, reason?: string): Promise<{ message: string }> {
        return ajax.post(`/api/organizations/${orgId}/join-requests/${requestId}/reject`, {
            reason,
        });
    }
}

export default new OrganizationService();
